<template>
  <div
    class="grid five-blocks-section critical-component-clear"
    :class="{ 'reverse-direction': settings.direction === 'rtl' }"
  >
    <div class="five-blocks-section__main-block col xs12 l32">
      <template v-if="isMobile ? mainBlock.mobileVideo : mainBlock.desktopVideo">
        <video-player
          v-if="!isMobile && mainBlock.desktopVideo"
          :id="mainBlock.desktopVideo.id"
          class="five-blocks-section__video desktop"
          :controls="{
            id: `${mainBlock.desktopVideo.id}-desktop`,
            muteButton: mainBlock.videoAudioControls,
            hide: true,
          }"
          :is-dark="mainBlock.videoTintDesktop === 'dark'"
          :image="mainBlock.desktopVideo.preview_image"
          :intersection-observer="{
            enable: mainBlock.videoAutoPlay,
          }"
          :sources="mainBlock.desktopVideo.sources"
          :poster="mainBlock.videoPoster"
          :attributes="mainBlock.enableAutoplay ? {
            autoplay: true,
          } : {}"
          :is-active="mainBlock.enableAutoplay ? true : false"
        />
        <video-player
          v-if="isMobile && mainBlock.mobileVideo"
          :id="mainBlock.mobileVideo.id"
          class="five-blocks-section__video mobile"
          :controls="{
            id: `${mainBlock.desktopVideo.id}-mobile`,
            muteButton: mainBlock.videoAudioControls,
          }"
          :is-dark="mainBlock.videoTintMobile === 'dark'"
          :image="mainBlock.mobileVideo.preview_image"
          :intersection-observer="{
            enable: mainBlock.videoAutoPlay,
          }"
          :sources="mainBlock.mobileVideo.sources"
          :poster="mainBlock.mobileVideoPoster"
          :attributes="mainBlock.enableAutoplay ? {
            autoplay: true,
          } : {}"
          :is-active="mainBlock.enableAutoplay ? true : false"
        />
      </template>

      <component
        :is="mainBlock.url ? 'a' : 'div'"
        v-else
        :href="$localizeUrl(mainBlock.url)"
      >
        <responsive-image
          v-if="!isMobile && mainBlock.desktopImage"
          class="five-blocks-section__image desktop"
          :class="{
            [`five-blocks-section__image${mainBlock.cardBackground}`]: mainBlock.cardBackground,
          }"
          :image="getImage(mainBlock.desktopImage)"
          ratio="720:877"
          sizes="(min-width: 1024px) 50vw, 100vw"
        />
        <responsive-image
          v-if="isMobile && (mainBlock.mobileImage || mainBlock.desktopImage)"
          class="five-blocks-section__image mobile"
          :class="{
            [`five-blocks-section__image${mainBlock.cardBackground}`]: mainBlock.cardBackground,
          }"
          :image="getImage(mainBlock.mobileImage || mainBlock.desktopImage)"
          ratio="720:877"
          sizes="(min-width: 1024px) 50vw, 100vw"
        />
      </component>

      <component
        :is="mainBlock.url ? 'a' : 'p'"
        v-if="mainBlock?.title"
        :href="$localizeUrl(mainBlock.url)"
        class="five-blocks-section__main-block-title"
        :style="`color:  ${mainBlock.textColor}`"
        :class="getTitleClasses(mainBlock)"
      >
        {{ mainBlock.title }}
      </component>

      <button
        v-if="mainBlock.enableHotspot && mainBlock.products &&
          mainBlock.products.length &&
          visibleProductsBlockIndex !== index
        "
        class="lookbook-images__atc"
        :class="[
          mainBlock.desktopVideo ? 'has-video-desktop' : '',
          mainBlock.mobileVideo ? 'has-video-mobile' : ''
        ]"
        type="button"
        @click="showProducts"
      >
        <icon-cart />

        <span
          class="
            lookbook-images__atc-label
            text-body-2xs-regular
          "
          v-text="$string('lookbook.shop_the_look')"
        />
      </button>
      <transition name="fade">
        <div
          v-if="mainBlock.products.length"
          v-show="visibleProductsBlockIndex >= 0"
          class="lookbook-images"
          :class="`ind-${visibleProductsBlockIndex}-${index}`"
        >
          <div
            v-if="!showBackInStock"
            class="lookbook-product-list-modal__header-wrapper"
          >
            <button
              v-if="viewedProduct?.id"
              class="lookbook-product-list-modal__close-atc"
              type="button"
              @click="closeAtc"
            >
              <icon-arrow-left />

              <span
                class="
                  lookbook-product-list-modal__close-atc-label
                  text-body-2xs-regular
                "
                v-text="$string('lookbook.return_to_look')"
              />
            </button>

            <h2
              v-else
              class="
                lookbook-product-list-modal__header
                text-body-2xs-regular
              "
              v-text="$string('lookbook.shop_the_look')"
            />
          </div>
          <div class="lookbook-images__product-cards-wrapper">
            <div class="lookbook-images__buttons-wrapper">
              <button
                v-if="blockConfigs?.productSelected"
                class="lookbook-images__close-atc"
                type="button"
                @click="closeAtc"
              >
                <icon-arrow-left />
                <span
                  class="
                    lookbook-images__close-atc-label
                    text-body-2xs-regular
                  "
                  v-text="
                    showBackInStock
                      ? $string('lookbook.back_to_look')
                      : $string('lookbook.return_to_look')
                  "
                />
              </button>
              <button
                v-else
                class="lookbook-images__products-close"
                type="button"
                @click="toggleProducts(-1)"
              >
                <span
                  class="visually-hidden"
                  v-text="$string('accessibility.close')"
                />
                <icon-close aria-hidden="true" />
              </button>
            </div>
            <div
              class="lookbook-images__carousel-wrapper"
              :class="{ 'grid-wrapper': blockConfigs?.productSelected }"
            >
              <splide
                ref="splide"
                class="lookbook-images__carousel"
                :has-track="false"
                :options="getSplideConfigs(mainBlock.products.length, index)"
              >
                <splide-track>
                  <splide-slide
                    v-for="(product, productIndex) of mainBlock.products"
                    :key="productIndex"
                  >
                    <product-card
                      :product="product"
                      card-layout="small"
                      :show-wishlist="false"
                      :enable-flat-image="false"
                      enable-variant-image
                      :show-badges="false"
                      :show-swatches="false"
                      :load-secondary-image="false"
                      :show-product-content="false"
                      :image-background-color="mainBlock.cardBackground"
                      :show-quick-add="!blockConfigs?.productSelected"
                      enable-image-carousel
                      :carousel-limit="4"
                      custom-atc
                      :product-card-location="productCardLocation"
                      :show-final-sale="false"
                      @custom-quick-add="handleAddToCart(productIndex, mainBlock.cardBackground)"
                    />
                  </splide-slide>
                </splide-track>
                <div class="lookbook-images__arrows splide__arrows">
                  <button
                    class="splide__arrow splide__arrow--prev"
                    type="button"
                  >
                    <span
                      class="visually-hidden"
                      v-text="$string('accessibility.carousel.previous')"
                    />
                    <icon-arrow-left />
                  </button>
                  <button
                    class="splide__arrow splide__arrow--next"
                    type="button"
                  >
                    <span
                      class="visually-hidden"
                      v-text="$string('accessibility.carousel.next')"
                    />
                    <icon-arrow-right />
                  </button>
                </div>
              </splide>
              <div
                v-if="blockConfigs?.productSelected"
                class="lookbook-images__product-details"
                :class="{
                  'bis-active': showBackInStock
                }"
              >
                <quick-add-to-cart
                  v-if="visibleProductsBlockIndex > -1 && blockConfigs.viewedProduct"
                  size-guide-position="top"
                  :number-of-sizes-per-row="7"
                  show-confirmation-button
                  show-view-product-button
                  :product="blockConfigs.viewedProduct"
                  :show-back-in-stock="showBackInStock"
                  show-product-details
                  is-stl-type
                  :product-card-location="productCardLocation"
                  :bis-settings="{
                    showCloseButton: false,
                    namespace: `lookbook-${blockConfigs.viewedProduct.handle}`,
                    marketingText: $string('product.back_in_stock.marketing_opt_in')
                  }"
                  @bis-active="handleBackInStockModal"
                />
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>

    <div class="grid five-blocks-section__small-blocks col xs12 l32">
      <template
        v-for="(block, index) of smallBlocks"
        :key="index"
      >
        <div
          v-if="block.products && block.products.length"
          class="five-blocks-section__small-block-wrapper col xs6 l16"
          :class="`desktop-alignment-${block.titlePositionDesktop}
          mobile-alignment-${block.titlePositionMobile}`"
        >
          <template
            v-for="(product, pindex) of block.products"
            :key="pindex"
          >
            <product-card
              :product="product"
              card-layout="small"
              :show-wishlist="false"
              enable-flat-image
              enable-variant-image
              get-shopify-product
              :show-badges="false"
              :show-swatches="false"
              show-info
              :load-secondary-image="false"
              :enable-secondary-image="false"
              show-product-content
              :image-background-color="block.cardBackground"
              show-quick-add
              :show-secondary-name="false"
              :show-final-sale="false"
              custom-atc
              source="five-block-section"
              :font-settings="getTitleClasses(block)"
              @custom-quick-add="handleProductClick(product, block.cardBackground)"
            />
          </template>
        </div>

        <component
          :is="block.url ? 'a' : 'div'"
          v-else
          :href="$localizeUrl(block.url)"
          class="five-blocks-section__small-block-wrapper col xs6 l16"
        >
          <responsive-image
            class="five-blocks-section__image desktop"
            :class="{
              [`five-blocks-section__image${block.cardBackground}`]: mainBlock.cardBackground,
            }"
            :image="getImage(block.desktopImage)"
            ratio="720:877"
            sizes="(min-width: 1024px) 50vw, 100vw"
          />

          <responsive-image
            class="five-blocks-section__image mobile"
            :class="{
              [`five-blocks-section__image${block.cardBackground}`]: mainBlock.cardBackground,
            }"
            :image="getImage(block.mobileImage || block.desktopImage)"
            ratio="720:877"
            sizes="(min-width: 1024px) 50vw, 100vw"
          />

          <div
            class="five-blocks-section__small-block-title"
            :style="`color: ${block.textColor}`"
            :class="
              `${block.titleFontDesktop}
              ${block.titleFontMobile}
              ${block.titleFontWeightDesktop}
              ${block.titleFontWeightMobile}
              ${block.titleFontFamily}
              desktop-alignment-${block.titlePositionDesktop}
              mobile-alignment-${block.titlePositionMobile}`"
            v-html="block.title"
          />
        </component>
      </template>
    </div>
  </div>
</template>

<script>

/**
 * Vue: five-blocks-section (five-blocks-section)
 * -----------------------------------------------------------------------------
 * 5 Blocks (Collage) Section.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapState } from 'vuex'
import ResponsiveImage from '~global/images/responsive-image'
import ProductCard from '~global/product-card/product-card'
import QuickAddToCart from '~async/quick-add-to-cart/quick-add-to-cart.vue'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import Btn from '~global/btn/btn'
import IconArrowLeft from '~icons/directional-navigation/chevron/left.svg'
import IconArrowRight from '~icons/directional-navigation/chevron/right.svg'
import IconClose from '~icons/general/close.svg'
import IconCart from '~icons/general/cart.svg'

export default {
  name: 'FiveBlocksSection',

  components: {
    ResponsiveImage,
    ProductCard,
    Splide,
    SplideSlide,
    SplideTrack,
    Btn,
    IconArrowLeft,
    IconArrowRight,
    IconClose,
    IconCart,
    QuickAddToCart,
    VideoPlayer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.video-player' */
        '~async/video-player/video-player'
      ),
    }),
  },

  props: {
    settings: {
      type: Object,
      default: () => ({}),
    },
    mainBlock: {
      type: Object,
      default: () => ({}),
    },
    smallBlocks: {
      type: Array,
      default: () => [],
    },
    productCardLocation: {
      type: Object,
      default: () => ({
        key: 'sectionHotspot',
      }),
    },
  },

  data() {
    return {
      colorScheme: 'white',
      blockConfigs: [],
      visibleProductsBlockIndex: -1,
      showBackInStock: false,
      showPagination: true,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
      lastAddedProductId: (state) => state.quickAddToCart.lastAddedProductId,
      shopTheLookSelectedProducts: (state) => state.shopTheLook.selectedProducts,
      shopTheLookBlockConfigs: (state) => state.shopTheLook.blockConfigs,
    }),
  },

  watch: {
    isMobile: {
      handler(newIsMobile) {
        if (
          (newIsMobile && this.mainBlock?.mobileVideo) ||
          (!newIsMobile && this.mainBlock?.desktopVideo)
        ) {
          this.loadStylesheet('video-player')
        }
      },
      immediate: true,
    },

    lastAddedProductId() {
      if (this.lastAddedProductId) {
        this.closeAtcWithId()
      }
    },
  },

  mounted() {
    this.initBlockObjects()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      loadStylesheet: 'components/loadStylesheet',
      setStlProducts: 'shopTheLook/setProducts',
      setStlBlockConfigs: 'shopTheLook/setBlockConfigs',
      setStlIndex: 'shopTheLook/setIndex',
      setLocation: 'shopTheLook/setLocation',
      openOverlay: 'overlays/open',
      setProductFormDrawerProduct: 'productFormDrawer/setProductFormDrawerProduct',
      setConfig: 'productFormDrawer/setConfig',
    }),

    /**
     * Handle product form drawer
     */
    handleProductClick(product, cardBackground) {
      if (product.loading) {
        return
      }

      product.cardBackground = cardBackground

      const config = {
        strings: {
          return: this.$string('shop_the_look.back_to_look'),
          back: this.$string('shop_the_look.back_to_look'),
        },
        bisNamespace: 'shop-the-look',
      }
      this.setProductFormDrawerProduct(product)
      this.setConfig(config)
      this.openOverlay({
        ignoreDismissed: true,
        namespace: 'product-form-drawer',
      })
    },

    /**
     * Handle add to cart.
     */
    handleAddToCart(productsIndex, cardBackground) {
      this.blockConfigs.viewedProduct =
        this.blockConfigs.selectedProducts[productsIndex]
      this.blockConfigs.productSelected = true
      this.blockConfigs.currentSplide = this.$refs.splide
      this.blockConfigs.viewedProductIndex = productsIndex
      this.blockConfigs.cardBackground = cardBackground
      this.blockConfigs.currentSplide.splide.event.on('resized', () => {
        this.blockConfigs.currentSplide
          .go(this.blockConfigs.viewedProductIndex)
      })
      this.blockConfigs.currentSplide.splide.event.on('moved', (newIndex) => {
        this.blockConfigs.viewedProductIndex = newIndex
        this.blockConfigs.viewedProduct =
          this.blockConfigs.selectedProducts[newIndex]
      })
    },

    /**
     * Initialize block objects
     */
    initBlockObjects() {
      this.blockConfigs.push({
        selectedProducts: [],
        visibleProductsBlockIndex: -1,
        productSelected: false,
        viewedProduct: {},
        viewedProductIndex: 0,
        currentSplide: {},
        cardBackground: '--color-brand-light-gray',
      })
    },

    /**
     * Checks variant ids of the products from the each block and resets
     * the block where id was found
     */
    closeAtcWithId() {
      if (this.mainBlock.products.some((product) => {
        const variantIds = product.variants.map((variant) => variant.id)
        return variantIds.includes(Number(this.lastAddedProductId))
      })) {
        this.closeAtc()
      }
    },

    /**
     * Close ATC and show products only
     */
    closeAtc() {
      if (this.blockConfigs?.currentSplide?.go) {
        this.blockConfigs.currentSplide.go(0)
      }
      if (this.blockConfigs) {
        this.blockConfigs.productSelected = false
        this.blockConfigs.viewedProduct = {}
      }
      if (this.showBackInStock) {
        this.showBackInStock = false
      }
    },

    /**
     * Show/switch/hide product list
     */
    toggleProducts(index) {
      this.blockConfigs.selectedProducts = this.mainBlock.products
      this.visibleProductsBlockIndex = index
      this.blockConfigs.forEach((_, configIndex) => {
        if (configIndex !== index) {
          this.closeAtc()
        }
      })
    },

    /**
      * Get video MP4 source object.
      * @param {Array} sources - Video sources.
      * @returns {Object}
      */
    getVideoSource(sources) {
      return sources.find((source) => source.format === 'mp4')
    },

    /**
     * Get splide configs per lookbook state
     * @param {Number} productCount - Products of lookbook.
     */
    getSplideConfigs(productCount) {
      const showPerView = productCount === 4 ? 4 : 3
      return {
        autoplay: false,
        type: 'slide',
        perPage: this.blockConfigs?.productSelected ? 1 : showPerView,
        direction: 'ltr',
        drag: false,
        speed: this.$timing('normal'),
        start: 0,
        pauseOnHover: false,
        arrows: false,
        pagination: false,
      }
    },

    /**
    * Get image source.
    * @param {Object|String} image - Image.
    * @returns {Object}
    */
    getImage(image) {
      if (image?.src) {
        return image
      }

      return {
        src: image,
      }
    },

    /**
     * Get main block title classes
     */
    getTitleClasses(block) {
      let classes = `
        ${block.titleFontDesktop}
        ${block.titleFontMobile}
        ${block.titleAlignmentMobile}
        ${block.titleAlignmentDesktop}
        ${block.titleFontWeightDesktop}
        ${block.titleFontWeightMobile}
        ${block.titleFontFamily}
      `

      if ((!this.isMobile && block.desktopVideo) || (this.isMobile && block.mobileVideo)) {
        classes += 'lifted'
      }

      return classes
    },

    /**
     * Show shop the look products
     * @param {Number} index - Index of the block.
     * @param {Object} block - Block object.
     */
    showProducts() {
      if (this.isMobile) {
        this.showOverlay(this.mainBlock, 'lookbook-product-list-modal')
      } else {
        this.toggleProducts(0)
      }
    },

    /**
    * Open product list overlay.
    */
    showOverlay(block, component) {
      this.blockConfigs.selectedProducts = this.mainBlock.products
      this.shopTheLookSelectedProducts = this.mainBlock.products
      this.blockConfigs[0].cardBackground = this.mainBlock.cardBackground
      this.shopTheLookBlockConfigs = this.blockConfigs
      this.setStlProducts(this.mainBlock.products)
      this.setStlBlockConfigs(this.blockConfigs)
      this.setStlIndex(0)
      this.setLocation(this.productCardLocation)
      this.openOverlay({
        ignoreDismissed: true,
        namespace: component,
      })
    },

    /**
     * Handles back in stock modal display
     * @param {Boolean} isActive - back in stock display state
     */
    handleBackInStockModal(isActive) {
      this.showBackInStock = isActive
    },
  },
}
</script>

<style lang="scss">
@import './five-blocks-section';
@import '../lookbook-images/lookbook-images';
@import '~global/product-form-drawer/product-form-drawer';
</style>
